'use server'

/**
 * Server Actions for Custom Avatar Management
 *
 * 커스텀 아바타 CRUD, 파일 업로드, 설정 관리를 위한 Server Actions
 */

import { auth } from '@clerk/nextjs/server'
import { revalidatePath, revalidateTag } from 'next/cache'
import type {
  CustomAvatar,
  CustomAvatarCreate,
  CustomAvatarUpdate,
  CustomAvatarSettingsUpdate,
  AvatarAnimationCreate,
  FileUploadResponse,
  AnimationUploadResponse,
  CustomScenario,
  CustomScenarioCreate,
  BodyType,
  ScenarioType,
  PlayMode,
} from '@/features/custom-avatar/types'

const NEXT_PUBLIC_BACKEND_URL = process.env.NEXT_PUBLIC_BACKEND_URL || 'http://backend:8500'

// ============ Helper Functions ============

async function getAuthHeaders() {
  const { userId } = await auth()

  if (!userId) {
    throw new Error('Unauthorized')
  }

  return {
    'Authorization': `Bearer ${userId}`,
    'X-User-Id': userId,
  }
}

function revalidateCustomAvatar(agentId: string) {
  revalidatePath(`/agents/${agentId}`)
  revalidatePath(`/agents/${agentId}/avatar`)
  revalidateTag('custom-avatar', 'max')
}

// ============ CRUD Operations ============

/**
 * 선택된 커스텀 아바타 조회
 */
export async function getCustomAvatar(agentId: string): Promise<CustomAvatar | null> {
  try {
    const headers = await getAuthHeaders()

    const response = await fetch(`${NEXT_PUBLIC_BACKEND_URL}/api/agents/${agentId}/custom-avatar`, {
      headers,
      cache: 'no-store',
    })

    if (!response.ok) {
      if (response.status === 404) {
        return null
      }
      throw new Error(`Failed to fetch custom avatar: ${response.status}`)
    }

    const data = await response.json()
    return data ? transformCustomAvatarResponse(data) : null
  } catch (error) {
    console.error('[Server Action] getCustomAvatar error:', error)
    throw error
  }
}

/**
 * 공개 커스텀 아바타 조회 (인증 불필요)
 * 슬러그 페이지에서 사용 - Clerk 인증 없이 커스텀 아바타 설정을 로드
 */
export async function getCustomAvatarPublic(agentId: string): Promise<CustomAvatar | null> {
  try {
    const response = await fetch(
      `${NEXT_PUBLIC_BACKEND_URL}/api/public/agents/${agentId}/custom-avatar`,
      {
        cache: 'no-store',
      }
    )

    if (!response.ok) {
      if (response.status === 404) {
        return null
      }
      throw new Error(`Failed to fetch public custom avatar: ${response.status}`)
    }

    const data = await response.json()
    return data ? transformCustomAvatarResponse(data) : null
  } catch (error) {
    console.error('[Server Action] getCustomAvatarPublic error:', error)
    return null
  }
}

/**
 * 에이전트의 모든 커스텀 아바타 목록 조회
 */
export async function listCustomAvatars(agentId: string): Promise<{
  avatars: CustomAvatar[]
  count: number
}> {
  try {
    const headers = await getAuthHeaders()

    const response = await fetch(`${NEXT_PUBLIC_BACKEND_URL}/api/agents/${agentId}/custom-avatars`, {
      headers,
      cache: 'no-store',
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch custom avatars: ${response.status}`)
    }

    const data = await response.json()
    return {
      avatars: (data.avatars || []).map(transformCustomAvatarResponse),
      count: data.count || 0,
    }
  } catch (error) {
    console.error('[Server Action] listCustomAvatars error:', error)
    throw error
  }
}

/**
 * 특정 커스텀 아바타 조회
 */
export async function getCustomAvatarById(
  agentId: string,
  avatarId: string
): Promise<CustomAvatar | null> {
  try {
    const headers = await getAuthHeaders()

    const response = await fetch(
      `${NEXT_PUBLIC_BACKEND_URL}/api/agents/${agentId}/custom-avatar/${avatarId}`,
      {
        headers,
        cache: 'no-store',
      }
    )

    if (!response.ok) {
      if (response.status === 404) {
        return null
      }
      throw new Error(`Failed to fetch custom avatar: ${response.status}`)
    }

    const data = await response.json()
    return data ? transformCustomAvatarResponse(data) : null
  } catch (error) {
    console.error('[Server Action] getCustomAvatarById error:', error)
    throw error
  }
}

/**
 * 커스텀 아바타 선택
 */
export async function selectCustomAvatar(
  agentId: string,
  avatarId: string
): Promise<boolean> {
  try {
    const headers = await getAuthHeaders()

    const response = await fetch(
      `${NEXT_PUBLIC_BACKEND_URL}/api/agents/${agentId}/custom-avatar/${avatarId}/select`,
      {
        method: 'PUT',
        headers,
      }
    )

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to select custom avatar: ${response.status} ${errorText}`)
    }

    revalidateCustomAvatar(agentId)
    return true
  } catch (error) {
    console.error('[Server Action] selectCustomAvatar error:', error)
    throw error
  }
}

/**
 * 특정 커스텀 아바타 삭제
 */
export async function deleteCustomAvatarById(
  agentId: string,
  avatarId: string
): Promise<boolean> {
  try {
    const headers = await getAuthHeaders()

    const response = await fetch(
      `${NEXT_PUBLIC_BACKEND_URL}/api/agents/${agentId}/custom-avatar/${avatarId}`,
      {
        method: 'DELETE',
        headers,
      }
    )

    if (!response.ok) {
      throw new Error(`Failed to delete custom avatar: ${response.status}`)
    }

    revalidateCustomAvatar(agentId)
    return true
  } catch (error) {
    console.error('[Server Action] deleteCustomAvatarById error:', error)
    throw error
  }
}

/**
 * 커스텀 아바타 생성
 */
export async function createCustomAvatar(
  agentId: string,
  formData: FormData
): Promise<CustomAvatar> {
  try {
    const headers = await getAuthHeaders()

    // FormData에서 데이터 추출
    const modelFile = formData.get('model_file') as File
    const name = formData.get('name') as string
    const bodyType = formData.get('body_type') as string
    const description = formData.get('description') as string | null

    // 새로운 FormData 생성 (Server Actions 환경에서 File 직렬화 문제 해결)
    const newFormData = new FormData()
    newFormData.append('name', name)
    newFormData.append('body_type', bodyType)
    if (description) {
      newFormData.append('description', description)
    }

    // File을 ArrayBuffer로 변환하여 Blob으로 추가
    const modelArrayBuffer = await modelFile.arrayBuffer()
    const modelBlob = new Blob([modelArrayBuffer], { type: modelFile.type || 'model/gltf-binary' })
    newFormData.append('model_file', modelBlob, modelFile.name || 'model.glb')

    const response = await fetch(`${NEXT_PUBLIC_BACKEND_URL}/api/agents/${agentId}/custom-avatar`, {
      method: 'POST',
      headers: {
        ...headers,
        // FormData는 Content-Type을 자동으로 설정하므로 명시하지 않음
      },
      body: newFormData,
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to create custom avatar: ${response.status} ${errorText}`)
    }

    const data = await response.json()
    revalidateCustomAvatar(agentId)

    return transformCustomAvatarResponse(data)
  } catch (error) {
    console.error('[Server Action] createCustomAvatar error:', error)
    throw error
  }
}

/**
 * 커스텀 아바타 수정
 */
export async function updateCustomAvatar(
  agentId: string,
  data: CustomAvatarUpdate
): Promise<CustomAvatar> {
  try {
    const headers = await getAuthHeaders()

    const formData = new FormData()
    if (data.name) formData.append('name', data.name)
    if (data.bodyType) formData.append('body_type', data.bodyType)
    if (data.description !== undefined) formData.append('description', data.description || '')

    const response = await fetch(`${NEXT_PUBLIC_BACKEND_URL}/api/agents/${agentId}/custom-avatar`, {
      method: 'PUT',
      headers,
      body: formData,
    })

    if (!response.ok) {
      throw new Error(`Failed to update custom avatar: ${response.status}`)
    }

    const result = await response.json()
    revalidateCustomAvatar(agentId)

    return transformCustomAvatarResponse(result)
  } catch (error) {
    console.error('[Server Action] updateCustomAvatar error:', error)
    throw error
  }
}

/**
 * 커스텀 아바타 삭제
 */
export async function deleteCustomAvatar(agentId: string): Promise<boolean> {
  try {
    const headers = await getAuthHeaders()

    const response = await fetch(`${NEXT_PUBLIC_BACKEND_URL}/api/agents/${agentId}/custom-avatar`, {
      method: 'DELETE',
      headers,
    })

    if (!response.ok) {
      throw new Error(`Failed to delete custom avatar: ${response.status}`)
    }

    revalidateCustomAvatar(agentId)
    return true
  } catch (error) {
    console.error('[Server Action] deleteCustomAvatar error:', error)
    throw error
  }
}

// ============ Presigned URL Upload (대용량 파일용) ============

/**
 * Presigned URL 생성 (Supabase Storage 직접 업로드용)
 */
export async function getUploadUrl(
  agentId: string,
  fileType: 'model' | 'thumbnail' | 'animation',
  filename: string
): Promise<{
  uploadUrl: string
  storagePath: string
  publicUrl: string
}> {
  try {
    const headers = await getAuthHeaders()

    const response = await fetch(
      `${NEXT_PUBLIC_BACKEND_URL}/api/agents/${agentId}/custom-avatar/upload-url?file_type=${fileType}&filename=${encodeURIComponent(filename)}`,
      {
        method: 'POST',
        headers,
      }
    )

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to get upload URL: ${response.status} ${errorText}`)
    }

    const data = await response.json()
    return {
      uploadUrl: data.upload_url,
      storagePath: data.storage_path,
      publicUrl: data.public_url,
    }
  } catch (error) {
    console.error('[Server Action] getUploadUrl error:', error)
    throw error
  }
}

/**
 * Supabase Storage에 파일 직접 업로드 후 애니메이션 생성
 */
export async function createAnimationFromStorage(
  agentId: string,
  data: {
    name: string
    scenarioType?: ScenarioType | null
    customScenarioId?: string | null
    playMode: PlayMode
    blendDuration?: number
    order?: number
    animationStoragePath: string
  }
): Promise<AnimationUploadResponse> {
  try {
    const headers = await getAuthHeaders()

    const queryParams = new URLSearchParams({
      name: data.name,
      play_mode: data.playMode,
      blend_duration: String(data.blendDuration || 0.2),
      order: String(data.order || 0),
      animation_storage_path: data.animationStoragePath,
    })

    // scenarioType과 customScenarioId 중 하나만 설정
    if (data.scenarioType) {
      queryParams.append('scenario_type', data.scenarioType)
    }
    if (data.customScenarioId) {
      queryParams.append('custom_scenario_id', data.customScenarioId)
    }

    const response = await fetch(
      `${NEXT_PUBLIC_BACKEND_URL}/api/agents/${agentId}/custom-avatar/animation/create-from-storage?${queryParams.toString()}`,
      {
        method: 'POST',
        headers,
      }
    )

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to create animation: ${response.status} ${errorText}`)
    }

    const result = await response.json()
    revalidateCustomAvatar(agentId)

    return {
      success: result.success,
      storagePath: result.storage_path,
      publicUrl: result.public_url,
      filename: result.filename,
      fileSize: result.file_size,
      animationId: result.animation_id,
      scenarioType: result.scenario_type as ScenarioType,
    }
  } catch (error) {
    console.error('[Server Action] createAnimationFromStorage error:', error)
    throw error
  }
}

/**
 * Supabase Storage에 파일 직접 업로드 후 아바타 생성
 * 또는 model_library_id로 라이브러리 모델 참조
 */
export async function createCustomAvatarFromStorage(
  agentId: string,
  data: {
    name: string
    bodyType: BodyType
    description?: string
    modelStoragePath?: string      // 직접 업로드한 모델 경로
    modelLibraryId?: string        // 라이브러리 모델 참조 (정규화)
    thumbnailUrl?: string          // 썸네일 URL (기본 아바타 커스터마이징 시)
  }
): Promise<CustomAvatar> {
  try {
    const headers = await getAuthHeaders()

    const queryParams = new URLSearchParams({
      name: data.name,
      body_type: data.bodyType,
    })

    // modelLibraryId가 우선 (정규화)
    if (data.modelLibraryId) {
      queryParams.append('model_library_id', data.modelLibraryId)
    } else if (data.modelStoragePath) {
      queryParams.append('model_storage_path', data.modelStoragePath)
    }

    // Server Action 직렬화로 인한 '$undefined' 문자열 방어
    if (data.description && data.description !== '$undefined') {
      queryParams.append('description', data.description)
    }

    // 썸네일 URL (기본 아바타 커스터마이징 시)
    if (data.thumbnailUrl && data.thumbnailUrl !== '$undefined') {
      queryParams.append('thumbnail_url', data.thumbnailUrl)
    }

    const response = await fetch(
      `${NEXT_PUBLIC_BACKEND_URL}/api/agents/${agentId}/custom-avatar/create-from-storage?${queryParams.toString()}`,
      {
        method: 'POST',
        headers,
      }
    )

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to create custom avatar: ${response.status} ${errorText}`)
    }

    const result = await response.json()
    revalidateCustomAvatar(agentId)

    return transformCustomAvatarResponse(result)
  } catch (error) {
    console.error('[Server Action] createCustomAvatarFromStorage error:', error)
    throw error
  }
}

// ============ File Upload Operations ============

/**
 * GLB 모델 업로드
 */
export async function uploadAvatarModel(
  agentId: string,
  formData: FormData
): Promise<FileUploadResponse> {
  try {
    const headers = await getAuthHeaders()

    // FormData에서 파일 추출 및 새 FormData 생성
    const modelFile = formData.get('model_file') as File
    const newFormData = new FormData()
    const modelArrayBuffer = await modelFile.arrayBuffer()
    const modelBlob = new Blob([modelArrayBuffer], { type: modelFile.type || 'model/gltf-binary' })
    newFormData.append('model_file', modelBlob, modelFile.name || 'model.glb')

    const response = await fetch(`${NEXT_PUBLIC_BACKEND_URL}/api/agents/${agentId}/custom-avatar/model`, {
      method: 'POST',
      headers,
      body: newFormData,
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to upload model: ${response.status} ${errorText}`)
    }

    const data = await response.json()
    revalidateCustomAvatar(agentId)

    return {
      success: data.success,
      storagePath: data.storage_path,
      publicUrl: data.public_url,
      filename: data.filename,
      fileSize: data.file_size,
    }
  } catch (error) {
    console.error('[Server Action] uploadAvatarModel error:', error)
    throw error
  }
}

/**
 * 썸네일 이미지 업로드
 */
export async function uploadAvatarThumbnail(
  agentId: string,
  formData: FormData
): Promise<FileUploadResponse> {
  try {
    const headers = await getAuthHeaders()

    // FormData에서 파일 추출 및 새 FormData 생성
    const thumbnailFile = formData.get('thumbnail_file') as File
    const newFormData = new FormData()
    const thumbnailArrayBuffer = await thumbnailFile.arrayBuffer()
    const thumbnailBlob = new Blob([thumbnailArrayBuffer], { type: thumbnailFile.type || 'image/png' })
    newFormData.append('thumbnail_file', thumbnailBlob, thumbnailFile.name || 'thumbnail.png')

    const response = await fetch(`${NEXT_PUBLIC_BACKEND_URL}/api/agents/${agentId}/custom-avatar/thumbnail`, {
      method: 'POST',
      headers,
      body: newFormData,
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to upload thumbnail: ${response.status} ${errorText}`)
    }

    const data = await response.json()
    revalidateCustomAvatar(agentId)

    return {
      success: data.success,
      storagePath: data.storage_path,
      publicUrl: data.public_url,
      filename: data.filename,
      fileSize: data.file_size,
    }
  } catch (error) {
    console.error('[Server Action] uploadAvatarThumbnail error:', error)
    throw error
  }
}

/**
 * 애니메이션 GLB 업로드
 */
export async function uploadAnimation(
  agentId: string,
  formData: FormData
): Promise<AnimationUploadResponse> {
  try {
    const headers = await getAuthHeaders()

    // FormData에서 데이터 추출
    const animationFile = formData.get('animation_file') as File
    const name = formData.get('name') as string
    const scenarioType = formData.get('scenario_type') as string
    const playMode = formData.get('play_mode') as string
    const blendDuration = formData.get('blend_duration') as string

    // 새 FormData 생성 (Server Actions 환경에서 File 직렬화 문제 해결)
    const newFormData = new FormData()
    newFormData.append('name', name)
    newFormData.append('scenario_type', scenarioType)
    newFormData.append('play_mode', playMode)
    newFormData.append('blend_duration', blendDuration)

    // File을 ArrayBuffer로 변환하여 Blob으로 추가
    const animationArrayBuffer = await animationFile.arrayBuffer()
    const animationBlob = new Blob([animationArrayBuffer], { type: animationFile.type || 'model/gltf-binary' })
    newFormData.append('animation_file', animationBlob, animationFile.name || 'animation.glb')

    const response = await fetch(`${NEXT_PUBLIC_BACKEND_URL}/api/agents/${agentId}/custom-avatar/animation`, {
      method: 'POST',
      headers,
      body: newFormData,
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to upload animation: ${response.status} ${errorText}`)
    }

    const data = await response.json()
    revalidateCustomAvatar(agentId)

    return {
      success: data.success,
      storagePath: data.storage_path,
      publicUrl: data.public_url,
      filename: data.filename,
      fileSize: data.file_size,
      animationId: data.animation_id,
      scenarioType: data.scenario_type as ScenarioType,
    }
  } catch (error) {
    console.error('[Server Action] uploadAnimation error:', error)
    throw error
  }
}

/**
 * 애니메이션 삭제
 */
export async function deleteAnimation(
  agentId: string,
  animationId: string
): Promise<boolean> {
  try {
    const headers = await getAuthHeaders()

    const response = await fetch(
      `${NEXT_PUBLIC_BACKEND_URL}/api/agents/${agentId}/custom-avatar/animation/${animationId}`,
      {
        method: 'DELETE',
        headers,
      }
    )

    if (!response.ok) {
      throw new Error(`Failed to delete animation: ${response.status}`)
    }

    revalidateCustomAvatar(agentId)
    return true
  } catch (error) {
    console.error('[Server Action] deleteAnimation error:', error)
    throw error
  }
}

// ============ Settings Operations ============

/**
 * 카메라/라이트/립싱크 설정 업데이트
 */
export async function saveAvatarSettings(
  agentId: string,
  settings: CustomAvatarSettingsUpdate
): Promise<CustomAvatar> {
  try {
    const headers = await getAuthHeaders()

    // snake_case로 변환
    const payload: Record<string, unknown> = {}

    if (settings.cameraConfig) {
      payload.camera_config = {
        fov: settings.cameraConfig.fov,
        position: settings.cameraConfig.position,
        rotation: settings.cameraConfig.rotation,
        near: settings.cameraConfig.near,
        far: settings.cameraConfig.far,
      }
    }

    if (settings.lightingConfig) {
      payload.lighting_config = {
        lights: settings.lightingConfig.lights.map(light => ({
          type: light.type,
          color: light.color,
          intensity: light.intensity,
          position: light.position,
          cast_shadow: light.castShadow,
          sky_color: light.skyColor,
          ground_color: light.groundColor,
        })),
        environment_hdr: settings.lightingConfig.environmentHdr,
      }
    }

    if (settings.lipsyncConfig) {
      payload.lipsync_config = {
        indices: settings.lipsyncConfig.indices,
        processing_type: settings.lipsyncConfig.processingType,
        config: settings.lipsyncConfig.config,
      }
    }

    if (settings.avatarTransform) {
      payload.avatar_transform = {
        scale: settings.avatarTransform.scale,
        position: settings.avatarTransform.position,
        rotation: settings.avatarTransform.rotation,
      }
    }

    if (settings.displayMode) {
      payload.display_mode = settings.displayMode
    }

    const response = await fetch(`${NEXT_PUBLIC_BACKEND_URL}/api/agents/${agentId}/custom-avatar/settings`, {
      method: 'PUT',
      headers: {
        ...headers,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })

    if (!response.ok) {
      throw new Error(`Failed to save settings: ${response.status}`)
    }

    const data = await response.json()
    revalidateCustomAvatar(agentId)

    return transformCustomAvatarResponse(data)
  } catch (error) {
    console.error('[Server Action] saveAvatarSettings error:', error)
    throw error
  }
}

/**
 * 등신 타입별 기본 설정 조회
 */
export async function getBodyTypeDefaults(bodyType: BodyType) {
  try {
    const headers = await getAuthHeaders()

    const response = await fetch(
      `${NEXT_PUBLIC_BACKEND_URL}/api/agents/custom-avatar/body-type-defaults?body_type=${bodyType}`,
      {
        headers,
        cache: 'force-cache',
      }
    )

    if (!response.ok) {
      throw new Error(`Failed to fetch body type defaults: ${response.status}`)
    }

    return response.json()
  } catch (error) {
    console.error('[Server Action] getBodyTypeDefaults error:', error)
    throw error
  }
}

// ============ Custom Scenario Operations ============

/**
 * 커스텀 시나리오 생성
 */
export async function createCustomScenario(
  agentId: string,
  data: CustomScenarioCreate
): Promise<CustomScenario> {
  try {
    const headers = await getAuthHeaders()

    const response = await fetch(
      `${NEXT_PUBLIC_BACKEND_URL}/api/agents/${agentId}/custom-avatar/scenarios`,
      {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      }
    )

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to create custom scenario: ${response.status} ${errorText}`)
    }

    const result = await response.json()
    revalidateCustomAvatar(agentId)

    return {
      id: result.id,
      customAvatarId: result.custom_avatar_id,
      name: result.name,
      label: result.label,
      description: result.description,
      createdAt: result.created_at,
    }
  } catch (error) {
    console.error('[Server Action] createCustomScenario error:', error)
    throw error
  }
}

/**
 * 커스텀 시나리오 목록 조회
 */
export async function listCustomScenarios(
  agentId: string
): Promise<CustomScenario[]> {
  try {
    const headers = await getAuthHeaders()

    const response = await fetch(
      `${NEXT_PUBLIC_BACKEND_URL}/api/agents/${agentId}/custom-avatar/scenarios`,
      {
        headers,
        cache: 'no-store',
      }
    )

    if (!response.ok) {
      throw new Error(`Failed to list custom scenarios: ${response.status}`)
    }

    const results = await response.json()
    return results.map((scenario: Record<string, unknown>) => ({
      id: scenario.id as string,
      customAvatarId: scenario.custom_avatar_id as string,
      name: scenario.name as string,
      label: scenario.label as string,
      description: scenario.description as string | undefined,
      createdAt: scenario.created_at as string,
    }))
  } catch (error) {
    console.error('[Server Action] listCustomScenarios error:', error)
    throw error
  }
}

/**
 * 커스텀 시나리오 삭제
 */
export async function deleteCustomScenario(
  agentId: string,
  scenarioId: string
): Promise<boolean> {
  try {
    const headers = await getAuthHeaders()

    const response = await fetch(
      `${NEXT_PUBLIC_BACKEND_URL}/api/agents/${agentId}/custom-avatar/scenarios/${scenarioId}`,
      {
        method: 'DELETE',
        headers,
      }
    )

    if (!response.ok) {
      throw new Error(`Failed to delete custom scenario: ${response.status}`)
    }

    revalidateCustomAvatar(agentId)
    return true
  } catch (error) {
    console.error('[Server Action] deleteCustomScenario error:', error)
    throw error
  }
}

// ============ Animation Order Operations ============

/**
 * 애니메이션 순서 일괄 변경
 */
export async function reorderAnimations(
  agentId: string,
  animationOrders: { animationId: string; order: number }[]
): Promise<boolean> {
  try {
    const headers = await getAuthHeaders()

    // snake_case로 변환
    const payload = animationOrders.map(item => ({
      animation_id: item.animationId,
      order: item.order,
    }))

    const response = await fetch(
      `${NEXT_PUBLIC_BACKEND_URL}/api/agents/${agentId}/custom-avatar/animations/reorder`,
      {
        method: 'PUT',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      }
    )

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to reorder animations: ${response.status} ${errorText}`)
    }

    revalidateCustomAvatar(agentId)
    return true
  } catch (error) {
    console.error('[Server Action] reorderAnimations error:', error)
    throw error
  }
}

/**
 * 단일 애니메이션 순서 변경
 */
export async function updateAnimationOrder(
  agentId: string,
  animationId: string,
  newOrder: number
): Promise<boolean> {
  try {
    const headers = await getAuthHeaders()

    const response = await fetch(
      `${NEXT_PUBLIC_BACKEND_URL}/api/agents/${agentId}/custom-avatar/animation/${animationId}/order?new_order=${newOrder}`,
      {
        method: 'PUT',
        headers,
      }
    )

    if (!response.ok) {
      throw new Error(`Failed to update animation order: ${response.status}`)
    }

    revalidateCustomAvatar(agentId)
    return true
  } catch (error) {
    console.error('[Server Action] updateAnimationOrder error:', error)
    throw error
  }
}

// ============ Model Update Operations ============

/**
 * 라이브러리 모델로 기존 아바타의 모델 교체
 *
 * 기존 설정(카메라, 조명, 립싱크, 시나리오 등)은 유지하고 모델만 교체합니다.
 */
export async function updateAvatarModel(
  agentId: string,
  avatarId: string,
  modelLibraryId: string
): Promise<CustomAvatar> {
  try {
    const headers = await getAuthHeaders()

    const response = await fetch(
      `${NEXT_PUBLIC_BACKEND_URL}/api/agents/${agentId}/custom-avatar/${avatarId}/model?model_library_id=${encodeURIComponent(modelLibraryId)}`,
      {
        method: 'PUT',
        headers,
      }
    )

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to update avatar model: ${response.status} ${errorText}`)
    }

    const data = await response.json()
    revalidateCustomAvatar(agentId)

    return transformCustomAvatarResponse(data)
  } catch (error) {
    console.error('[Server Action] updateAvatarModel error:', error)
    throw error
  }
}

// ============ Transform Functions ============

function transformCustomAvatarResponse(data: Record<string, unknown>): CustomAvatar {
  // 커스텀 시나리오 변환
  const customScenarios = ((data.custom_scenarios as Record<string, unknown>[]) || []).map(scenario => ({
    id: scenario.id as string,
    customAvatarId: scenario.custom_avatar_id as string,
    name: scenario.name as string,
    label: scenario.label as string,
    description: scenario.description as string | undefined,
    createdAt: scenario.created_at as string,
  }))

  // 시나리오 맵 생성 (조인용)
  const scenarioMap = new Map(customScenarios.map(s => [s.id, s]))

  return {
    id: data.id as string,
    agentId: data.agent_id as string,
    userId: data.user_id as string,
    name: data.name as string,
    description: data.description as string | undefined,
    bodyType: data.body_type as BodyType,
    thumbnailUrl: data.thumbnail_url as string | undefined,
    modelUrl: data.model_url as string,
    modelStoragePath: data.model_storage_path as string,
    cameraConfig: transformCameraConfig(data.camera_config as Record<string, unknown> | undefined),
    lightingConfig: transformLightingConfig(data.lighting_config as Record<string, unknown> | undefined),
    lipsyncConfig: transformLipsyncConfig(data.lipsync_config as Record<string, unknown> | undefined),
    customAnimations: (data.custom_animations as unknown[]) || [],
    isActive: data.is_active as boolean,
    createdAt: data.created_at as string,
    updatedAt: data.updated_at as string,
    animations: ((data.animations as Record<string, unknown>[]) || []).map(anim => {
      const customScenarioId = anim.custom_scenario_id as string | null
      const customScenario = customScenarioId ? scenarioMap.get(customScenarioId) : undefined

      // 서버에서 이미 조인된 custom_scenario가 있으면 사용
      const serverCustomScenario = anim.custom_scenario as Record<string, unknown> | undefined
      const resolvedCustomScenario = serverCustomScenario ? {
        id: serverCustomScenario.id as string,
        customAvatarId: serverCustomScenario.custom_avatar_id as string,
        name: serverCustomScenario.name as string,
        label: serverCustomScenario.label as string,
        description: serverCustomScenario.description as string | undefined,
        createdAt: serverCustomScenario.created_at as string,
      } : customScenario

      return {
        id: anim.id as string,
        customAvatarId: anim.custom_avatar_id as string,
        name: anim.name as string,
        scenarioType: (anim.scenario_type as ScenarioType | null) || null,
        customScenarioId: customScenarioId,
        animationUrl: anim.animation_url as string,
        animationStoragePath: anim.animation_storage_path as string,
        playMode: (anim.play_mode as PlayMode) || 'once',
        blendDuration: (anim.blend_duration as number) || 0.2,
        order: (anim.order as number) || 0,
        createdAt: anim.created_at as string,
        customScenario: resolvedCustomScenario,
      }
    }),
    customScenarios,

    // web 모드 avatarTransform
    avatarTransform: transformAvatarTransform(data.avatar_transform as Record<string, unknown> | undefined),

    // kiosk 모드 설정
    kioskCameraConfig: data.kiosk_camera_config
      ? transformCameraConfig(data.kiosk_camera_config as Record<string, unknown>)
      : undefined,
    kioskLightingConfig: data.kiosk_lighting_config
      ? transformLightingConfig(data.kiosk_lighting_config as Record<string, unknown>)
      : undefined,
    kioskLipsyncConfig: data.kiosk_lipsync_config
      ? transformLipsyncConfig(data.kiosk_lipsync_config as Record<string, unknown>)
      : undefined,
    kioskAvatarTransform: data.kiosk_avatar_transform
      ? transformAvatarTransform(data.kiosk_avatar_transform as Record<string, unknown>)
      : undefined,
  }
}

function transformAvatarTransform(data?: Record<string, unknown>) {
  if (!data) return undefined

  return {
    scale: (data.scale as { x: number; y: number; z: number }) || { x: 1, y: 1, z: 1 },
    position: (data.position as { x: number; y: number; z: number }) || { x: 0, y: 0, z: 0 },
    rotation: (data.rotation as { x: number; y: number; z: number }) || { x: 0, y: 0, z: 0 },
  }
}

function transformCameraConfig(data?: Record<string, unknown>) {
  if (!data || Object.keys(data).length === 0) {
    return {
      fov: 40,
      position: { x: 0, y: 10, z: 36 },
      rotation: { x: 0, y: 0, z: 0 },
      near: 0.1,
      far: 200,
    }
  }

  return {
    fov: (data.fov as number) || 40,
    position: (data.position as { x: number; y: number; z: number }) || { x: 0, y: 10, z: 36 },
    rotation: (data.rotation as { x: number; y: number; z: number }) || { x: 0, y: 0, z: 0 },
    near: (data.near as number) || 0.1,
    far: (data.far as number) || 200,
  }
}

function transformLightingConfig(data?: Record<string, unknown>) {
  if (!data || Object.keys(data).length === 0) {
    return {
      lights: [],
      environmentHdr: '/hdr/panoramalow.hdr',
    }
  }

  return {
    lights: ((data.lights as Record<string, unknown>[]) || []).map(light => ({
      type: light.type as 'DirectionalLight' | 'HemisphereLight' | 'PointLight' | 'AmbientLight',
      color: (light.color as number) || 0xffffff,
      intensity: (light.intensity as number) || 1,
      position: light.position as { x: number; y: number; z: number } | undefined,
      castShadow: light.cast_shadow as boolean | undefined,
      skyColor: light.sky_color as number | undefined,
      groundColor: light.ground_color as number | undefined,
    })),
    environmentHdr: (data.environment_hdr as string) || '/hdr/panoramalow.hdr',
  }
}

function transformLipsyncConfig(data?: Record<string, unknown>) {
  if (!data || Object.keys(data).length === 0) {
    return {
      indices: [],
      processingType: 'filter' as const,
      config: {},
    }
  }

  return {
    indices: (data.indices as number[]) || [],
    processingType: (data.processing_type as 'filter' | 'range_mapping' | 'vrm_filter') || 'filter',
    config: (data.config as Record<string, unknown>) || {},
  }
}
